// Ported from dev-scrips-pm-react src/billing/pages/invoice-detail-page.tsx (PROD-3312 appointment Billing tab): the
// "Cancel this invoice?" AlertDialog and its handler. pm-react writes it inline in the page and keeps
// `useCancellationReasons(cancelOpen)` above the page's early returns (below them it crashed with React #310). As its
// own component the hook list is the dialog's, which is stable on every render, and the value-set is still only
// fetched while the dialog is open.
import { useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  BridgeSelect as Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/billing/bridge';
import { useCancelInvoice, useCancellationReasons } from '@/billing/services/use-invoice';
import { REASON_SELECT_CONTENT_CLASS } from '@/billing/lib/reason-select';

interface InvoiceCancelDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invoiceId: string;
}

export function InvoiceCancelDialog({ open, onOpenChange, invoiceId }: InvoiceCancelDialogProps) {
  const cancelMutation = useCancelInvoice();
  // PROD-1795: the chosen value is a reason `id` from the value-set, never a typed GUID.
  const [cancelReason, setCancelReason] = useState('');
  const cancelReasons = useCancellationReasons(open);
  const reasonOptions = cancelReasons.data ?? [];
  // Settled, no error, and still nothing to choose — distinct from "still loading", which must not render as empty.
  const hasNoReasons = !cancelReasons.isLoading && !cancelReasons.isError && reasonOptions.length === 0;

  const onConfirmCancel = async () => {
    if (!invoiceId || !cancelReason) return;
    try {
      await cancelMutation.mutateAsync({ invoiceId, reasonId: cancelReason });
      onOpenChange(false);
      setCancelReason('');
    } catch {
      /* Global toast surfaces failure; dialog stays open so the user can retry. */
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Cancel this invoice?</AlertDialogTitle>
          <AlertDialogDescription>
            Cancelling will move the invoice to the Cancelled status and halt any further payments or claim activity.
            Select a cancellation reason to proceed.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="space-y-2 px-1">
          <label className="mb-2 block text-sm font-medium leading-5 text-text-secondary">Reason</label>
          <Select value={cancelReason} onValueChange={(v) => setCancelReason(v ?? '')}>
            {/* pm-react has no aria-label here (the <label> above is not associated); the PA adds one so the
                combobox has an accessible name. */}
            <SelectTrigger className="w-full" aria-label="Reason">
              <SelectValue
                placeholder={
                  cancelReasons.isLoading
                    ? 'Loading reasons…'
                    : hasNoReasons
                      ? 'No reasons configured'
                      : 'Select a reason'
                }
              >
                {reasonOptions.find((r) => r.id === cancelReason)?.value}
              </SelectValue>
            </SelectTrigger>
            <SelectContent className={REASON_SELECT_CONTENT_CLASS}>
              {reasonOptions
                .filter((r) => r.id)
                .map((r) => (
                  <SelectItem key={r.id} value={r.id as string}>
                    {r.value ?? r.id}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
          {cancelReasons.isError && (
            <p className="text-xs leading-4 text-status-error">Could not load cancellation reasons. Close and retry.</p>
          )}
          {/* An org with no configured reasons would otherwise get a picker with nothing in it and a permanently
              disabled Confirm — a dead end. Naming the cause beats leaving the user clicking a disabled button. */}
          {hasNoReasons && (
            <p className="text-xs leading-4 text-text-muted">
              No cancellation reasons are configured for this organization — contact your administrator.
            </p>
          )}
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={cancelMutation.isPending}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirmCancel}
            disabled={!cancelReason || cancelMutation.isPending}
            className="bg-status-error hover:bg-status-error/90 text-white"
          >
            {cancelMutation.isPending ? 'Cancelling…' : 'Cancel invoice'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
